import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  const category = useMemo(() => {
    if (!movie) return null;
    return categories.find((c) => c.slug === movie.category);
  }, [movie]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.category === movie.category)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">资源未找到</h2>
            <p className="text-foreground-500 mb-6">该影视资源可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer hover:bg-primary-600"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || movie.category, href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: '首页',
                      item: 'https://www.yayacinema.com/',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: category?.name || movie.category,
                      item: `https://www.yayacinema.com/category/${movie.category}`,
                    },
                    {
                      '@type': 'ListItem',
                      position: 3,
                      name: movie.title,
                    },
                  ],
                }),
              }}
            />

            <div className="flex flex-col lg:flex-row gap-6 md:gap-10 mb-10">
              <div className="flex-shrink-0 w-full lg:w-[340px]">
                <div className="relative rounded-lg overflow-hidden bg-background-200 aspect-[2/3] lg:h-[480px]">
                  <img
                    src={movie.image}
                    alt={`${movie.title} - 丫丫影院免费在线观看`}
                    title={`${movie.title} - 丫丫影院在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 bg-accent-100 text-accent-800 text-sm px-2.5 py-1 rounded-md">
                    <i className="ri-star-fill text-accent-500"></i>
                    {movie.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-sm text-foreground-500">{movie.subCategory}</span>
                  <span className="text-sm text-foreground-500">{movie.episodes}</span>
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">基本信息</h3>
                    <div className="flex flex-col gap-1.5 text-sm">
                      <p className="text-foreground-600">
                        <span className="text-foreground-400">导演：</span>{movie.director}
                      </p>
                      <p className="text-foreground-600">
                        <span className="text-foreground-400">主演：</span>{movie.actors.join('、')}
                      </p>
                      <p className="text-foreground-600">
                        <span className="text-foreground-400">地区：</span>{movie.region}
                      </p>
                      <p className="text-foreground-600">
                        <span className="text-foreground-400">年份：</span>{movie.year}
                      </p>
                      <p className="text-foreground-600">
                        <span className="text-foreground-400">更新状态：</span>{movie.updateInfo}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">立即观看</h3>
                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/category/${movie.category}`}
                        className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line text-lg"></i>
                        在线播放
                      </Link>
                      <p className="text-xs text-foreground-400">
                        本站所有资源均来自互联网，仅供学习交流使用
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} size="sm" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${movie.title} - 丫丫影院在线观看`,
              description: movie.description,
              datePublished: `${movie.year}-01-01`,
              author: {
                '@type': 'Organization',
                name: '丫丫影院',
              },
            }),
          }}
        />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}